import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, TextField, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ProfilePictureUpload from 'components/contacts/contactDetails/editContactDetails/ProfilePictureUpload';
import PhoneInputField from 'components/common/PhoneInputField';

// Styles for text fields
const textFieldStyles = {
    backgroundColor: '#f7f7f7',
    borderRadius: '12px',
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '& fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '&:hover fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '& input': {
            borderRadius: '12px',
            padding: '10px 12px',
        },
        '& input::placeholder': {
            fontSize: '0.8rem',
            fontWeight: '300',
        },
    },
};

// Define lead fields - matching detail page order
const LEAD_FIELDS = [
    { label: 'Name', name: 'leadName', required: true },
    { label: 'Email', name: 'leadEmail', required: true },
    { label: 'Job Title', name: 'leadJobTitle' },
    { label: 'Company', name: 'leadCompany' },
    { label: 'CRM Link', name: 'crmLink' },
    { label: 'Website', name: 'website' },
    { label: 'Location', name: 'location' },
    { label: 'Instagram', name: 'instagram' },
    { label: 'LinkedIn', name: 'linkedin' },
    { label: 'Card Source', name: 'cardSource' }
];

const TeamV2LeadDetailsEdit = ({ lead, onUpdate, onCancel }) => {
    const [formData, setFormData] = useState({
        leadName: lead?.leadName || '',
        leadEmail: lead?.leadEmail || '',
        leadJobTitle: lead?.leadJobTitle || '',
        leadCompany: lead?.leadCompany || '',
        crmLink: lead?.crmLink || '',
        exportedTo: lead?.exportedTo || '',
        leadPhoneNumber: lead?.leadPhoneNumber || '',
        website: lead?.website || '',
        linkedin: lead?.linkedin || '',
        instagram: lead?.instagram || '',
        location: lead?.location || '',
        cardSource: lead?.cardSource || '',
        bio: lead?.bio || '',
        notes: lead?.notes || '',
        department: lead?.department || '',
        role: lead?.role || '',
        profilePicture: null
    });
    const [loading, setLoading] = useState(false);
    const [phoneError, setPhoneError] = useState('');

    // Update form data when lead changes
    useEffect(() => {
        setFormData({
            leadName: lead?.leadName || '',
            leadEmail: lead?.leadEmail || '',
            leadJobTitle: lead?.leadJobTitle || '',
            leadCompany: lead?.leadCompany || '',
            crmLink: lead?.crmLink || '',
            exportedTo: lead?.exportedTo || '',
            leadPhoneNumber: lead?.leadPhoneNumber || '',
            website: lead?.website || '',
            linkedin: lead?.linkedin || '',
            instagram: lead?.instagram || '',
            location: lead?.location || '',
            cardSource: lead?.cardSource || '',
            bio: lead?.bio || '',
            notes: lead?.notes || '',
            department: lead?.department || '',
            role: lead?.role || '',
            profilePicture: null
        });
    }, [lead]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle phone number changes
    const handlePhoneChange = (value) => {
        setFormData(prev => ({
            ...prev,
            leadPhoneNumber: value
        }));
        setPhoneError('');
    };

    // Update profile picture
    const handleProfilePictureUpdate = (pictureBlob) => {
        setFormData(prev => ({
            ...prev,
            profilePicture: pictureBlob
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const requiredFields = ['leadName', 'leadEmail'];
        const isValid = requiredFields.every(field => formData[field]?.trim());

        if (isValid) {
            try {
                // For now, just call the onUpdate callback with the form data
                // In a real implementation, you would call an API here
                if (onUpdate) {
                    onUpdate({
                        ...lead,
                        ...formData
                    });
                }
            } catch (error) {
                console.error('Error updating lead:', error);
                alert('Failed to update lead. Please try again.');
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
            alert('Please fill in required fields (Name and Email)');
        }
    };

    return (
        <Box sx={{ mt: 3, overflow: 'hidden' }}>
            <form onSubmit={handleSubmit}>
                {/* Profile Picture Upload */}
                <ProfilePictureUpload
                    initialProfilePicture={lead?.profilePictureUrl}
                    onProfilePictureUpdate={handleProfilePictureUpdate}
                />

                {/* Lead Fields */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {LEAD_FIELDS.map((field) => (
                        <Box key={field.name}>
                            <Typography
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#4f4f4f',
                                    marginBottom: '10px'
                                }}
                            >
                                {field.label}
                                {field.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                            </Typography>
                            <TextField
                                fullWidth
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleInputChange}
                                placeholder={field.label}
                                required={field.required}
                                sx={{
                                    ...textFieldStyles,
                                    '& .MuiInputBase-root': {
                                        height: '48px'
                                    }
                                }}
                            />
                        </Box>
                    ))}

                    {/* Phone Number Field */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Phone Number
                        </Typography>
                        <PhoneInputField
                            value={formData.leadPhoneNumber}
                            onChange={handlePhoneChange}
                            error={phoneError}
                            placeholder="Enter phone number"
                            containerStyle={{ marginBottom: '10px' }}
                        />
                    </Box>

                    {/* Exported to Field */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Exported to
                        </Typography>
                        <FormControl
                            fullWidth
                            sx={{
                                ...textFieldStyles,
                                '& .MuiInputBase-root': {
                                    height: '48px'
                                }
                            }}
                        >
                            <Select
                                value={formData.exportedTo || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, exportedTo: e.target.value }))}
                                displayEmpty
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: '12px',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            >
                                <MenuItem value="">Select CRM</MenuItem>
                                <MenuItem value="Salesforce">Salesforce</MenuItem>
                                <MenuItem value="HubSpot">HubSpot</MenuItem>
                                <MenuItem value="Pipedrive">Pipedrive</MenuItem>
                                <MenuItem value="None">None</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Bio Field (Full Width) */}
                    <Box sx={{ gridColumn: '1 / span 2', marginTop: 2 }}>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Bio
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            name="bio"
                            value={formData.bio || ''}
                            onChange={handleInputChange}
                            placeholder="Bio"
                            sx={{
                                ...textFieldStyles,
                                '& .MuiInputBase-root': {
                                    height: 'auto',
                                    minHeight: '90px'
                                }
                            }}
                        />
                    </Box>

                    {/* Notes Field (Full Width) */}
                    <Box sx={{ gridColumn: '1 / span 2', marginTop: 2 }}>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Notes
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            name="notes"
                            value={formData.notes || ''}
                            onChange={handleInputChange}
                            placeholder="Notes"
                            sx={{
                                ...textFieldStyles,
                                '& .MuiInputBase-root': {
                                    height: 'auto',
                                    minHeight: '90px'
                                }
                            }}
                        />
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                        marginTop: 3,
                        paddingTop: 2,
                        borderTop: '1px solid #e0e0e0'
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                        sx={{
                            backgroundColor: 'white',
                            color: '#4f4f4f',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            '&:hover': {
                                backgroundColor: 'white',
                                boxShadow: 2,
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                            backgroundColor: '#000',
                            color: '#fff',
                            borderRadius: '20px',
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            '&:hover': {
                                backgroundColor: '#000',
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default TeamV2LeadDetailsEdit;